import { useState, useEffect, useCallback } from 'react';
import type { TimerConfig } from 'model';
import { useTimer } from 'hooks/useTimer';
import { TimerDisplay } from './TimerDisplay';
import { TimerControls } from './TimerControls';
import { TimerSettings } from './TimerSettings';
import { ManPage } from 'components/ManPage/ManPage';
import {
  initAudioContext,
  playWarningBeep,
  playSetEndBeep,
  playRestEndBeep,
  playCompletionBeep,
  playSoundOption,
  playStartBeep
} from 'utils/audio';
import './WorkoutTimer.css';

const LOCAL_STORAGE_KEY = 'workout-timer-config';

interface Preset {
  id: string;
  name: string;
  setDuration: number;
  intervalDuration: number;
  totalSets: number;
}

const presets: Preset[] = [
  { id: 'hangboard', name: 'Hangboard (10s/40s × 5)', setDuration: 10, intervalDuration: 40, totalSets: 10 },
  { id: 'kettlebell', name: 'Kettlebell (30s/30s × 10)', setDuration: 30, intervalDuration: 30, totalSets: 12 },
  { id: 'calisthenics', name: 'Calisthenics (45s/15s × 5)', setDuration: 45, intervalDuration: 15, totalSets: 5 },
  { id: 'infinite', name: 'Infinite (45s/15s × ∞)', setDuration: 45, intervalDuration: 15, totalSets: -1 }
];

const defaultConfig: TimerConfig = {
  setDuration: 10,
  intervalDuration: 40,
  totalSets: 5,
  warningTime: 3,
  warningSound: 'triple-beep',
  setEndSound: 'single-beep',
  playCompletionSound: true
};

function loadConfig(): TimerConfig {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      return { ...defaultConfig, ...JSON.parse(stored) };
    }
  } catch {
    // Ignore localStorage errors
  }
  return defaultConfig;
}

function saveConfig(config: TimerConfig): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Ignore localStorage errors
  }
}

export function WorkoutTimer(): JSX.Element {
  const [config, setConfig] = useState<TimerConfig>(loadConfig);
  const [audioInitialised, setAudioInitialised] = useState(false);

  // Audio event handlers
  const handleWarning = useCallback(() => {
    playSoundOption(config.warningSound);
  }, [config.warningSound]);

  const handleSetEnd = useCallback(() => {
    playSoundOption(config.setEndSound);
  }, [config.setEndSound]);

  const handleRestEnd = useCallback(() => {
    playRestEndBeep();
  }, []);

  const handleComplete = useCallback(() => {
    if (config.playCompletionSound) {
      playCompletionBeep();
    }
  }, [config.playCompletionSound]);

  const { state, start, pause, reset } = useTimer(config, {
    onWarning: handleWarning,
    onSetEnd: handleSetEnd,
    onRestEnd: handleRestEnd,
    onComplete: handleComplete
  });

  // Save config when it changes
  useEffect(() => {
    saveConfig(config);
  }, [config]);

  // Handle start with audio initialisation
  const handleStart = useCallback(() => {
    if (!audioInitialised) {
      initAudioContext();
      setAudioInitialised(true);
    }
    playStartBeep();
    start();
  }, [audioInitialised, start]);

  const handlePause = useCallback(() => {
    playStartBeep();
    pause();
  }, [pause]);

  const handleReset = useCallback(() => {
    playStartBeep();
    reset();
  }, [reset]);

  const isIdle = state.phase === 'idle';
  const isComplete = state.phase === 'complete';
  const canEditSettings = isIdle || isComplete;

  return (
    <ManPage
      name="timer"
      manual="Interval Workout"
      sections={[{ heading: 'NAME', body: 'timer - interval workout timer' }]}
    >
      <div className="workout-timer-container">
        <TimerDisplay
          timeRemaining={isIdle ? config.setDuration : state.timeRemaining}
          phase={state.phase}
          currentSet={state.currentSet}
          totalSets={config.totalSets}
        />

        <TimerControls
          isRunning={state.isRunning}
          isPaused={state.isPaused}
          isIdle={isIdle}
          isComplete={isComplete}
          onStart={handleStart}
          onPause={handlePause}
          onReset={handleReset}
        />

        <div className="timer-settings-section">
          <div className="timer-settings-header">
            <h3>Settings</h3>
            <div className="dropdown">
              <button
                className="btn btn-secondary dropdown-toggle"
                type="button"
                data-toggle="dropdown"
                disabled={!canEditSettings}
              >
                Presets
              </button>
              <div className="dropdown-menu dropdown-menu-right">
                {presets.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setConfig({
                        ...config,
                        setDuration: preset.setDuration,
                        intervalDuration: preset.intervalDuration,
                        totalSets: preset.totalSets
                      });
                    }}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <TimerSettings config={config} onChange={setConfig} disabled={!canEditSettings} />
          {!canEditSettings && <p className="timer-settings-note">Pause or reset to edit settings</p>}
        </div>
      </div>
    </ManPage>
  );
}
